import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
export class AssetLoader {

    constructor() {

        this.loadingManager = new THREE.LoadingManager();

        this.gltfLoader = new GLTFLoader(this.loadingManager);
        this.textureLoader = new THREE.TextureLoader(this.loadingManager);
        this.audioLoader = new THREE.AudioLoader(this.loadingManager);

        this.models = new Map();
        this.textures = new Map();
        this.audio = new Map();

        this.loadingManager.onStart = (url, itemsLoaded, totalItems) => {
            console.log("Started loading" + url);
        }

        this.loadingManager.onProgress = (url, itemsLoaded, itemsTotal) => {

            const progress = (itemsLoaded / itemsTotal) * 100;

            console.log(`Loading: ${progress}%`);

        };

        this.loadingManager.onError = (url) => {
            console.error(`There was an error loading ${url}`);
        };

    }

    async loadGroup(assetList) {

        const promises = assetList.map(asset => {
            if (asset.type === 'gltf') {
                return new Promise((resolve, reject) => {
                    this.gltfLoader.load(
                        asset.path,
                        (gltf) => {
                            this.models.set(asset.name, gltf);
                            resolve(gltf);
                        },
                        undefined,
                        (error) => reject(error)
                    );
                });
            }

            if (asset.type === 'texture') {
                return new Promise((resolve, reject) => {
                    this.textureLoader.load(
                        asset.path,
                        (texture) => {
                            this.textures.set(asset.name, texture);
                            resolve(texture);
                        },
                        undefined,
                        (error) => reject(error)
                    );
                });
            }

            if(asset.type === 'audio'){
                return new Promise((resolve, reject) => {
                    this.audioLoader.load(
                        asset.path,
                        (audio) => {
                            this.audio.set(asset.name, audio);
                            resolve(audio);
                        },
                        undefined,
                        (error) => reject(error)
                    );
                });
            }


            return Promise.resolve();
        });

        return Promise.all(promises);
    }

    disposeGroup(groupName) {
        const assets = this.groups.get(groupName);
        assets.forEach(asset => {
            if (asset.type === 'gltf') {
                asset.data.scene.traverse((child) => {
                    if (child.isMesh) {
                        child.geometry.dispose();
                        child.material.dispose();
                        if (child.material.map) child.material.map.dispose();
                    }
                });
            }
            this.models.delete(asset.name);
        });
    }

    async loadManifestGroup(groupKey) {
        const response = await fetch('/assetData/Assets.json');
        const manifest = await response.json();
        const data = manifest[groupKey];

        if (!data) {
            console.error(`Group ${groupKey} not found in manifest`);
            return;
        }

        // If it's an object (like lobby_assets), flatten all its arrays into one list
        let assetList = [];
        if (Array.isArray(data)) {
            assetList = data;
        } else {
            // Combines environment, furniture, and interactive_pieces into one array
            Object.values(data).forEach(subArray => {
                if (Array.isArray(subArray)) assetList.push(...subArray);
            });
        }

        return await this.loadGroup(assetList);
    }


    getModel(name) {
        if (this.models.has(name)) return this.models.get(name);
 
        console.warn(`AssetLoader: "${name}" not loaded — returning proxy.`);
        return this.createProxy(name);
    }
 
    /**
     * Creates a minimal GLTF-like object with a placeholder mesh.
     * Colour is randomised so proxies are visually obvious in-scene.
     */
    createProxy(name) {
        const color = new THREE.Color().setHSL(Math.random(), 0.7, 0.5);
        const geo   = new THREE.BoxGeometry(0.5, 0.5, 0.5);
        const mat   = new THREE.MeshStandardMaterial({ color, wireframe: false });
        const mesh  = new THREE.Mesh(geo, mat);
        mesh.name   = `PROXY_${name}`;
 
        // Wrap in a Group so it matches gltf.scene interface
        const scene = new THREE.Group();
        scene.add(mesh);
 
        return { scene, isProxy: true };
    }

    getTexture(name){
        return this.textures.get(name);
    }

    getAudio(name){
        return this.audio.get(name);
    }
}